import React, { useMemo, useState, useEffect } from "react";
import { Button } from '../ui/button';
import { toast } from '../ui/use-toast';
import { apiRequest } from '../../config/api';
import { useAuth } from '../../contexts/PhpAuthContext';
import { FileText, Printer, Download, AlertTriangle, CheckCircle2, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import MedicalHistoryForm from './MedicalHistoryForm';
import DentalExaminationForm from './DentalExaminationForm';
import ProcedureTrackingForm from './ProcedureTrackingForm';
import { pdf } from '@react-pdf/renderer';
import PatientRecordPDF from '../pdf/PatientRecordPDF';

// Tooth numbering constants (same as DentalExaminationForm)
const adultTeethNumbers = [8, 7, 6, 5, 4, 3, 2, 1, 1, 2, 3, 4, 5, 6, 7, 8];
const childTeethNumbers = ['E', 'D', 'C', 'B', 'A', 'A', 'B', 'C', 'D', 'E'];

const ReviewSubmitPage = ({ 
  patientId,
  patientName,
  appointmentId, // Optional: ID of the appointment this submission is for
  medicalHistory,
  dentalExamination,
  procedures,
  onBack,
  onSubmit,
  hideDialog = false,
  doctorName = null // Optional: Doctor name for "Submitted By" display (when viewing existing submission)
}) => {
  const { profile } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [submitStep, setSubmitStep] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  
  // Edit modals
  const [editSection, setEditSection] = useState(null);
  
  // Local state for data (will be updated after edits)
  const [currentMedicalHistory, setCurrentMedicalHistory] = useState(medicalHistory);
  const [currentDentalExamination, setCurrentDentalExamination] = useState(dentalExamination);
  const [currentProcedures, setCurrentProcedures] = useState(procedures);
  
  // State for patient info
  const [patientInfo, setPatientInfo] = useState({ 
    name: patientName, 
    id: patientId,
    phone: null,
    address: null
  });
  // State for appointment details
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  
  // Fetch patient name if we have patientId but no patientName
  useEffect(() => {
    const fetchPatientInfo = async () => {
      if (patientId) {
        try {
          const response = await apiRequest(`get_patients.php?patient_id=${patientId}`);
          if (response.success && response.patients && response.patients.length > 0) {
            const patient = response.patients[0];
            // Use full_name from the API response (patients table uses full_name, not name)
            const name = patient.full_name || `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || `Patient #${patientId}`;
            setPatientInfo({ 
              name, 
              id: patientId,
              phone: patient.phone || null,
              address: patient.address || null
            });
          } else {
            setPatientInfo({ name: `Patient #${patientId}`, id: patientId, phone: null, address: null });
          }
        } catch (error) {
          console.error('Error fetching patient info:', error);
          setPatientInfo({ name: `Patient #${patientId}`, id: patientId, phone: null, address: null });
        }
      } else if (patientName) {
        setPatientInfo({ name: patientName, id: patientId, phone: null, address: null });
      } else if (patientId) {
        setPatientInfo({ name: `Patient #${patientId}`, id: patientId, phone: null, address: null });
      }
    };
    
    fetchPatientInfo();
  }, [patientId, patientName]);

  // Fetch appointment details if appointmentId is provided
  useEffect(() => {
    const fetchAppointmentDetails = async () => {
      if (appointmentId) {
        try {
          const response = await apiRequest(`get_appointments_list.php?appointment_id=${appointmentId}`);
          if (response.success && response.appointments && response.appointments.length > 0) {
            const appointment = response.appointments.find(apt => apt.id === parseInt(appointmentId)) || response.appointments[0];
            setAppointmentDetails({
              id: appointment.id,
              referenceId: appointment.appointment_reference_id || null,
              date: appointment.appointment_date || null,
              time: appointment.appointment_time || null,
              status: appointment.status || null
            });
          } else {
            setAppointmentDetails(null);
          }
        } catch (error) {
          console.error('Error fetching appointment details:', error);
          setAppointmentDetails(null);
        }
      } else {
        setAppointmentDetails(null);
      }
    };
    
    fetchAppointmentDetails();
  }, [appointmentId]);
  
  // Update local state when props change
  useEffect(() => {
    setCurrentMedicalHistory(medicalHistory);
    setCurrentDentalExamination(dentalExamination);
    setCurrentProcedures(procedures);
  }, [medicalHistory, dentalExamination, procedures]);

  // Fetch data if props are null (e.g., when opening in view mode)
  useEffect(() => {
    const fetchDataIfNeeded = async () => {
      if (patientId && (!medicalHistory || !dentalExamination || !procedures)) {
        try {
          // Include appointment_id for medical history if available
          const medicalHistoryUrl = appointmentId 
            ? `get_medical_history.php?patient_id=${patientId}&appointment_id=${appointmentId}`
            : `get_medical_history.php?patient_id=${patientId}`;
          
          const [mhData, deData, ptData] = await Promise.all([
            apiRequest(medicalHistoryUrl).catch(() => ({ success: false })),
            apiRequest(`get_dental_examination.php?patient_id=${patientId}`).catch(() => ({ success: false })),
            apiRequest(`get_procedure_tracking.php?patient_id=${patientId}`).catch(() => ({ success: false }))
          ]);

          if (mhData.success && mhData.medical_history) {
            setCurrentMedicalHistory(mhData.medical_history);
          } else if (mhData.success && mhData.history) {
            // Fallback for backward compatibility
            setCurrentMedicalHistory(mhData.history);
          }
          if (deData.success && deData.examination) {
            setCurrentDentalExamination(deData.examination);
          }
          if (ptData.success && ptData.tracking) {
            setCurrentProcedures({
              rows: ptData.tracking.procedures || [],
              summary: ptData.tracking.summary || { total: 0, paid: 0, due: 0 }
            });
          }
        } catch (error) {
          console.error('Error fetching patient data:', error);
        }
      }
    };

    fetchDataIfNeeded();
  }, [patientId, appointmentId, medicalHistory, dentalExamination, procedures]);

  // Format helpers
  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return "Not specified";
      return d.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "Not specified";
    }
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(Number(value || 0))
      .replace("₹", "₹ ");

  const getGradeColor = (grade) => {
    switch (grade) {
      case "A": return "bg-green-700/20 border-green-700 text-green-300";
      case "B": return "bg-green-200/20 border-green-200 text-green-600";
      case "C": return "bg-orange-200/20 border-orange-200 text-orange-300";
      case "D": return "bg-red-400/20 border-red-400 text-red-400";
      case "E": return "bg-red-800/30 border-red-800 text-red-300";
      default: return hideDialog ? "bg-gray-800 border-gray-600 text-gray-400" : "bg-gray-100 border-gray-300 text-gray-500";
    }
  };

  // Abbreviate status text
  const abbreviateStatus = (status) => {
    if (!status) return "";
    const statusLower = status.toLowerCase().trim();
    if (statusLower === "crown") return "Cr";
    if (statusLower === "fractured") return "Fr";
    if (statusLower === "root canal treated") return "R";
    if (statusLower === "intact") return "I";
    if (statusLower === "caries") return "C";
    if (statusLower === "filled") return "F";
    if (statusLower === "missing") return "M";
    // Default: return first letter
    return status.charAt(0).toUpperCase();
  };

  // Render dental findings grid exactly like Dental Examination form
  const renderDentalFindingsGrid = () => {
    // Get patient type from dental examination
    const patientType = currentDentalExamination?.patient_type || "Adult";
    const isChild = patientType === "Child";
    
    // Get tooth numbers and grid size based on patient type
    const teethNumbers = isChild ? childTeethNumbers : adultTeethNumbers;
    const gridSize = isChild ? 10 : 16;
    
    // Convert findings array to grade/status arrays
    const upperFindings = findings?.upper || [];
    const lowerFindings = findings?.lower || [];
    
    // Create arrays for grades and status (pad to gridSize)
    const upperGrades = Array(gridSize).fill("").map((_, idx) => 
      upperFindings[idx]?.grade || ""
    );
    const upperStatus = Array(gridSize).fill("").map((_, idx) => 
      upperFindings[idx]?.status || ""
    );
    const lowerGrades = Array(gridSize).fill("").map((_, idx) => 
      lowerFindings[idx]?.grade || ""
    );
    const lowerStatus = Array(gridSize).fill("").map((_, idx) => 
      lowerFindings[idx]?.status || ""
    );
    
    // Count marked items
    const markedCount = {
      g: [...upperGrades, ...lowerGrades].filter(g => g).length,
      s: [...upperStatus, ...lowerStatus].filter(s => s).length
    };

    return (
      <div className="mt-4">
        {/* Header with Patient Type Toggle */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h4 className={`text-lg font-semibold ${hideDialog ? 'text-white' : 'text-gray-900'}`}>Dental Findings</h4>
            {/* Show only the selected button */}
            <button
              type="button"
              disabled
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                hideDialog 
                  ? 'bg-blue-600 text-white shadow-lg' 
                  : 'bg-blue-500 text-white shadow-md'
              }`}
            >
              {isChild ? "Child" : "Adult"}
            </button>
          </div>
          <span className={`rounded px-2 py-1 text-xs ${hideDialog ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
            Marked: {markedCount.g} grades / {markedCount.s} status
          </span>
        </div>

        {/* Upper Jaw Section */}
        <div className="mb-6">
          <div className={`flex items-center justify-between text-xs mb-2 ${hideDialog ? 'text-gray-400' : 'text-gray-500'}`}>
            <span>Upper (Right → Left)</span>
          </div>
          
          {/* Upper Status Row */}
          <div className="grid gap-1.5 mb-1.5" style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}>
            {upperStatus.slice(0, gridSize).map((status, idx) => (
              <div 
                key={`us-${idx}`} 
                className={`text-center text-xs font-medium min-h-[20px] flex items-center justify-center ${
                  hideDialog ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                {abbreviateStatus(status)}
              </div>
            ))}
          </div>
          
          {/* Upper Grade cells */}
          <div className="grid gap-1.5 mb-2" style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}>
            {upperGrades.slice(0, gridSize).map((grade, idx) => (
              <div
                key={`ug-${idx}`}
                className={`h-10 rounded-md border text-sm font-medium flex items-center justify-center ${getGradeColor(grade)} ${grade ? "shadow-sm" : ""}`}
              >
                {grade || "-"}
              </div>
            ))}
          </div>

          {/* Upper tooth numbers */}
          <div className="grid gap-1.5 mb-5" style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}>
            {teethNumbers.map((num, idx) => (
              <div key={`un-${idx}`} className={`text-center text-sm font-medium select-none ${hideDialog ? 'text-gray-400' : 'text-gray-500'}`}>
                {num}
              </div>
            ))}
          </div>
        </div>

        {/* Lower Jaw Section */}
        <div className="mb-6">
          <div className={`text-xs mb-2 ${hideDialog ? 'text-gray-400' : 'text-gray-500'}`}>Lower (Right ← Left)</div>
          
          {/* Lower tooth numbers */}
          <div className="grid gap-1.5 mb-2" style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}>
            {teethNumbers.map((num, idx) => (
              <div key={`ln-${idx}`} className={`text-center text-sm font-medium select-none ${hideDialog ? 'text-gray-400' : 'text-gray-500'}`}>
                {num}
              </div>
            ))}
          </div>
          
          {/* Lower Grade cells */}
          <div className="grid gap-1.5 mb-1.5" style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}>
            {lowerGrades.slice(0, gridSize).map((grade, idx) => (
              <div
                key={`lg-${idx}`}
                className={`h-10 rounded-md border text-sm font-medium flex items-center justify-center ${getGradeColor(grade)} ${grade ? "shadow-sm" : ""}`}
              >
                {grade || "-"}
              </div>
            ))}
          </div>
          
          {/* Lower Status Row */}
          <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}>
            {lowerStatus.slice(0, gridSize).map((status, idx) => (
              <div 
                key={`ls-${idx}`} 
                className={`text-center text-xs font-medium min-h-[20px] flex items-center justify-center ${
                  hideDialog ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                {abbreviateStatus(status)}
              </div>
            ))}
          </div>
        </div>

        {/* Grade Legend */}
        <div className="mt-6">
          <h4 className={`font-medium text-sm mb-3 ${hideDialog ? 'text-white' : 'text-gray-900'}`}>GRADE LEGEND</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-6 text-sm">
            <div className="flex items-center">
              <span className="w-5 h-5 rounded bg-green-700/20 border border-green-700 mr-2 flex-shrink-0"></span>
              <span className={hideDialog ? 'text-gray-300' : 'text-gray-700'}>A — No issue</span>
            </div>
            <div className="flex items-center">
              <span className="w-5 h-5 rounded bg-green-200/20 border border-green-200 mr-2 flex-shrink-0"></span>
              <span className={hideDialog ? 'text-gray-300' : 'text-gray-700'}>B — Mild</span>
            </div>
            <div className="flex items-center">
              <span className="w-5 h-5 rounded bg-orange-200/20 border border-orange-200 mr-2 flex-shrink-0"></span>
              <span className={hideDialog ? 'text-gray-300' : 'text-gray-700'}>C — Moderate</span>
            </div>
            <div className="flex items-center">
              <span className="w-5 h-5 rounded bg-red-400/20 border border-red-400 mr-2 flex-shrink-0"></span>
              <span className={hideDialog ? 'text-gray-300' : 'text-gray-700'}>D — Severe</span>
            </div>
            <div className="flex items-center">
              <span className="w-5 h-5 rounded bg-red-800/30 border border-red-800 mr-2 flex-shrink-0"></span>
              <span className={hideDialog ? 'text-gray-300' : 'text-gray-700'}>E — Extensive</span>
            </div>
          </div>
        </div>

        {/* Status Abbreviation Legend */}
        <div className="mt-4">
          <h4 className={`font-medium text-sm mb-3 ${hideDialog ? 'text-white' : 'text-gray-900'}`}>STATUS ABBREVIATIONS</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-6 text-sm">
            <div className="flex items-center">
              <span className={hideDialog ? 'text-gray-300' : 'text-gray-700'}>I — Intact</span>
            </div>
            <div className="flex items-center">
              <span className={hideDialog ? 'text-gray-300' : 'text-gray-700'}>C — Caries</span>
            </div>
            <div className="flex items-center">
              <span className={hideDialog ? 'text-gray-300' : 'text-gray-700'}>F — Filled</span>
            </div>
            <div className="flex items-center">
              <span className={hideDialog ? 'text-gray-300' : 'text-gray-700'}>M — Missing</span>
            </div>
            <div className="flex items-center">
              <span className={hideDialog ? 'text-gray-300' : 'text-gray-700'}>Cr — Crown</span>
            </div>
            <div className="flex items-center">
              <span className={hideDialog ? 'text-gray-300' : 'text-gray-700'}>Fr — Fractured</span>
            </div>
            <div className="flex items-center">
              <span className={hideDialog ? 'text-gray-300' : 'text-gray-700'}>R — Root Canal Treated</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Get findings from dental examination
  const findings = useMemo(() => {
    if (currentDentalExamination?.findings) {
      if (typeof currentDentalExamination.findings === 'string') {
        try {
          return JSON.parse(currentDentalExamination.findings);
        } catch {
          return { upper: [], lower: [] };
        }
      }
      return currentDentalExamination.findings;
    }
    return { upper: [], lower: [] };
  }, [currentDentalExamination]);

  // Get procedure rows
  const procedureRows = useMemo(() => {
    if (currentProcedures?.rows && Array.isArray(currentProcedures.rows)) {
      return currentProcedures.rows;
    }
    if (currentProcedures?.procedures && Array.isArray(currentProcedures.procedures)) {
      return currentProcedures.procedures;
    }
    return [];
  }, [currentProcedures]);

  // Get medical problems
  const medicalProblems = useMemo(() => {
    if (!currentMedicalHistory?.medical_problems) return [];
    try {
      const problems = typeof currentMedicalHistory.medical_problems === 'string' 
        ? JSON.parse(currentMedicalHistory.medical_problems) 
        : currentMedicalHistory.medical_problems;
      return Array.isArray(problems) ? problems : [];
    } catch {
      return [];
    }
  }, [currentMedicalHistory]);

  // Get trigger factors
  const triggerFactors = useMemo(() => {
    if (!currentDentalExamination?.trigger_factors) return [];
    try {
      const factors = typeof currentDentalExamination.trigger_factors === 'string'
        ? JSON.parse(currentDentalExamination.trigger_factors)
        : currentDentalExamination.trigger_factors;
      return Array.isArray(factors) ? factors : [];
    } catch {
      return [];
    }
  }, [currentDentalExamination]);

  // Validation issues
  const issues = useMemo(() => {
    const list = [];
    
    if (!currentDentalExamination?.chief_complaint) {
      list.push({
        section: "Dental Examination",
        msg: "Chief complaint is missing.",
        idx: 3,
      });
    }

    if (currentMedicalHistory?.surgery_hospitalized === 'Yes' && !currentMedicalHistory?.surgery_details) {
      list.push({
        section: "Medical History",
        msg: "Add details for surgery/hospitalization.",
        idx: 2,
      });
    }

    if (currentMedicalHistory?.fever_cold_cough === 'Yes' && !currentMedicalHistory?.symptoms_details) {
      list.push({
        section: "Medical History",
        msg: "Add current symptoms details.",
        idx: 2,
      });
    }

    if (currentMedicalHistory?.abnormal_bleeding === 'Yes' && !currentMedicalHistory?.abnormal_bleeding_details) {
      list.push({
        section: "Medical History",
        msg: "Add details for abnormal bleeding.",
        idx: 2,
      });
    }

    if (currentMedicalHistory?.taking_medicine === 'Yes' && !currentMedicalHistory?.medicine_details) {
      list.push({
        section: "Medical History",
        msg: "List medicines (name, dose, frequency).",
        idx: 2,
      });
    }

    if (currentMedicalHistory?.allergic_medication === 'Yes' && !currentMedicalHistory?.allergy_details) {
      list.push({
        section: "Medical History",
        msg: "Specify medication allergies and reactions.",
        idx: 2,
      });
    }

    return list;
  }, [currentDentalExamination, currentMedicalHistory]);

  // Calculate totals
  const totals = useMemo(() => {
    const total = procedureRows.reduce((sum, r) => sum + Number(r.total || 0), 0);
    const paid = procedureRows.reduce((sum, r) => sum + Number(r.paid || 0), 0);
    const due = total - paid;
    return { total, paid, due };
  }, [procedureRows]);

  const hasDue = totals.due > 0;

  // Open confirmation modal
  const openConfirm = () => {
    if (!currentDentalExamination?.chief_complaint) {
      setSubmitError("Chief complaint is required.");
      setEditSection(3);
      return;
    }
    setSubmitError("");
    setConfirmOpen(true);
  };

  // Submit handler
  const doSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");
    setSubmitStep("Submitting…");

    try {
      // Step 1: Generate PDF
      setSubmitStep("Generating PDF…");
      
      // Generate filename - Standard format: Patient-Record-{PatientName}-{AppointmentReferenceID}-{Date}-{Timestamp}.pdf
      // IMPORTANT: Appointment ID is REQUIRED for unique filename generation to prevent overwrites
      const finalAppointmentId = appointmentId || appointmentDetails?.id || appointmentDetails?.referenceId;
      
      if (!finalAppointmentId) {
        console.error('ReviewSubmitPage: CRITICAL - No appointment ID available for filename generation!');
        // Fallback: Use timestamp-based unique ID
        const fallbackId = `NO-APT-${Date.now()}`;
        console.warn(`ReviewSubmitPage: Using fallback ID: ${fallbackId}`);
      }
      
      // Use appointment reference ID (user-friendly) or numeric ID as fallback
      const appointmentReferenceId = appointmentDetails?.referenceId || finalAppointmentId || `NO-APT-${Date.now()}`;
      const date = new Date().toISOString().split('T')[0];
      const sanitizedPatientName = (patientName || 'Patient').replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-');
      
      // Add high-precision timestamp (milliseconds + random) to ensure absolute uniqueness
      // Even if same appointment is submitted twice in same millisecond, random ensures uniqueness
      // This prevents overwrites even for the same appointment ID
      const timestamp = Date.now();
      const randomSuffix = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
      const uniqueId = `${timestamp}-${randomSuffix}`;
      
      // Final filename MUST include appointment ID + unique timestamp to prevent overwrites
      const filename = `Patient-Record-${sanitizedPatientName}-${appointmentReferenceId}-${date}-${uniqueId}.pdf`;
      
      // Log for debugging - verify appointment ID is included
      console.log('ReviewSubmitPage: PDF filename generated:', {
        filename,
        appointmentId: finalAppointmentId,
        appointmentReferenceId,
        patientName: sanitizedPatientName,
        timestamp,
        uniqueId
      });

      // Get patient info for PDF
      const pdfPatientInfo = {
        name: patientInfo?.name || patientName || 'N/A',
        phone: patientInfo?.phone || 'N/A',
        address: patientInfo?.address || 'N/A',
      };

      // Validate appointment ID before generating PDF (already declared above, reuse it)
      // This ensures each PDF is uniquely associated with a specific appointment
      if (!finalAppointmentId) {
        console.warn('ReviewSubmitPage: Warning - No appointment ID provided. PDF will be generated without unique appointment reference.');
        toast({
          title: 'Warning',
          description: 'No appointment ID found. PDF will be generated without appointment reference.',
          variant: 'default',
        });
      }
      
      // Create PDF document with validated appointment details
      // Each appointment must have a unique ID to prevent PDF overwrites
      const doc = (
        <PatientRecordPDF
          patientInfo={pdfPatientInfo}
          appointmentDetails={{
            referenceId: appointmentDetails?.referenceId || finalAppointmentId || 'N/A',
            id: finalAppointmentId || appointmentDetails?.id || 'N/A',
          }}
          medicalHistory={currentMedicalHistory}
          dentalExamination={currentDentalExamination}
          procedures={procedures || { rows: procedureRows }}
          profile={profile || {}}
        />
      );

      // Generate PDF blob
      const pdfBlob = await pdf(doc).toBlob();

      // Step 2: Upload PDF to server
      setSubmitStep("Uploading PDF…");
      
      const formData = new FormData();
      formData.append('pdf', pdfBlob, filename);
      if (appointmentId) {
        formData.append('appointment_id', appointmentId);
      }
      formData.append('filename', filename);
      // Add patient name for folder organization
      if (patientName) {
        formData.append('patient_name', patientName);
      }

      const token = localStorage.getItem('session_token');
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://test.gugandentalcare.com/api/api';

      const uploadResponse = await fetch(`${API_BASE_URL}/upload_patient_record_pdf.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!uploadResponse.ok) {
        const uploadError = await uploadResponse.json();
        throw new Error(uploadError.error || 'Failed to upload PDF');
      }

      const uploadData = await uploadResponse.json();
      const pdfUrl = uploadData.url || uploadData.path;

      // Step 3: Save all data to backend with PDF URL
      setSubmitStep("Saving patient record…");
      
      const submitData = {
        patient_id: patientId,
        appointment_id: appointmentId || null, // Include appointment_id if provided
        pdf_url: pdfUrl, // Include PDF URL
        medical_history: currentMedicalHistory,
        dental_examination: currentDentalExamination,
        procedures: {
          rows: procedureRows,
          summary: totals
        }
      };

      const data = await apiRequest('submit_patient_record.php', {
        method: 'POST',
        body: JSON.stringify(submitData)
      });

      if (data.success) {
        setSubmitStep("All done!");
        
        toast({
          title: 'Success',
          description: 'Patient record submitted successfully',
          variant: 'default'
        });

        // Close confirmation modal
        setConfirmOpen(false);
        
        // Clear all localStorage/sessionStorage entries related to patient forms
        if (typeof window !== "undefined") {
          try {
            // Clear procedure tracking drafts for this patient
            const draftKeyBase = "dental:procedureTracking";
            if (patientId) {
              // Clear specific patient draft
              const patientDraftKey = `${draftKeyBase}:${patientId}:draft`;
              localStorage.removeItem(patientDraftKey);
              
              // Also clear any drafts with visitId pattern
              const visitDraftKey = `${draftKeyBase}:${appointmentId || patientId}:draft`;
              localStorage.removeItem(visitDraftKey);
            }
            
            // Clear all procedure tracking drafts (in case of any orphaned entries)
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && key.startsWith(draftKeyBase)) {
                keysToRemove.push(key);
              }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
            
            // Clear sessionStorage as well (if any form data is stored there)
            if (sessionStorage.length > 0) {
              const sessionKeysToRemove = [];
              for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && (key.includes('dental') || key.includes('procedure') || key.includes('medical') || key.includes('patient'))) {
                  sessionKeysToRemove.push(key);
                }
              }
              sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
            }
          } catch (error) {
            console.error('Error clearing storage:', error);
          }
        }
        
        // Reset all form data to default/empty
        setCurrentMedicalHistory(null);
        setCurrentDentalExamination(null);
        setCurrentProcedures([]);
        
        // Call onSubmit callback if provided (this will close the wizard)
        if (onSubmit) {
          onSubmit(data);
        }
      } else {
        throw new Error(data.error || 'Failed to submit record');
      }
    } catch (err) {
      setSubmitError(err?.message || "Failed to submit record");
      setSubmitting(false);
      setSubmitStep("");
    }
  };

  const printPage = () => {
    if (typeof window !== "undefined") {
      window.print();
    }
  };

  const exportJSON = () => {
    try {
      // Get doctor name from profile
      const doctorName = profile?.full_name || profile?.name || "Not specified";
      
      // Get visit date from procedures (most recent visit date or first row's visit date)
      let visitDate = null;
      if (procedureRows && procedureRows.length > 0) {
        // Sort by visitDate descending to get most recent
        const sortedRows = [...procedureRows].sort((a, b) => {
          const dateA = a.visitDate ? new Date(a.visitDate).getTime() : 0;
          const dateB = b.visitDate ? new Date(b.visitDate).getTime() : 0;
          return dateB - dateA;
        });
        visitDate = sortedRows[0]?.visitDate || null;
      }
      
      // Get appointment date and time
      const appointmentDate = appointmentDetails?.date || null;
      const appointmentTime = appointmentDetails?.time || null;
      const appointmentDateTime = appointmentDate && appointmentTime 
        ? `${appointmentDate} ${appointmentTime}` 
        : appointmentDate || null;
      
      const data = {
        // Patient Information
        patientId,
        patientName: patientInfo.name || patientName,
        
        // Doctor Information
        doctorName,
        doctorId: profile?.id || null,
        
        // Appointment Information
        appointmentId: appointmentId || null,
        appointmentReferenceId: appointmentDetails?.referenceId || null,
        appointmentDate: appointmentDate,
        appointmentTime: appointmentTime,
        appointmentDateTime: appointmentDateTime,
        appointmentStatus: appointmentDetails?.status || null,
        
        // Visit Information
        visitDate: visitDate,
        submittedAt: new Date().toISOString(),
        
        // Medical Records
        medicalHistory: currentMedicalHistory,
        dentalExamination: currentDentalExamination,
        procedures: {
          rows: procedureRows,
          summary: totals
        },
        
        // Metadata
        exportedAt: new Date().toISOString(),
        exportVersion: "1.0"
      };
      
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const filename = `patient-record-${patientId || 'new'}-${appointmentDetails?.referenceId || appointmentId || 'no-appt'}-${new Date().toISOString().split('T')[0]}.json`;
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to export JSON',
        variant: 'destructive'
      });
    }
  };

  // Handle edit section
  const handleEdit = (sectionIdx) => {
    setEditSection(sectionIdx);
    if (sectionIdx === 2) {
      setEditMedicalHistory(medicalHistory);
    } else if (sectionIdx === 3) {
      setEditDentalExamination(dentalExamination);
    } else if (sectionIdx === 4) {
      setEditProcedures(procedures);
    }
  };

  // Refetch data after edit
  const refetchData = async () => {
    try {
      // Fetch all data - include appointment_id for medical history if available
      const medicalHistoryUrl = appointmentId 
        ? `get_medical_history.php?patient_id=${patientId}&appointment_id=${appointmentId}`
        : `get_medical_history.php?patient_id=${patientId}`;
      
      const [mhData, deData, ptData] = await Promise.all([
        apiRequest(medicalHistoryUrl).catch(() => ({ success: false })),
        apiRequest(`get_dental_examination.php?patient_id=${patientId}`).catch(() => ({ success: false })),
        apiRequest(`get_procedure_tracking.php?patient_id=${patientId}`).catch(() => ({ success: false }))
      ]);

      // Update state with fetched data
      if (mhData.success && mhData.medical_history) {
        setCurrentMedicalHistory(mhData.medical_history);
      } else if (mhData.success && mhData.history) {
        // Fallback for backward compatibility
        setCurrentMedicalHistory(mhData.history);
      }
      if (deData.success && deData.examination) {
        setCurrentDentalExamination(deData.examination);
      }
      if (ptData.success && ptData.tracking) {
        setCurrentProcedures({
          rows: ptData.tracking.procedures || [],
          summary: ptData.tracking.summary || { total: 0, paid: 0, due: 0 }
        });
      }
    } catch (error) {
      console.error('Error refetching data:', error);
    }
  };

  // Content wrapper
  const contentWrapper = (
    <>
      {/* Print styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 1.5cm;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          html, body { 
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            height: auto !important;
          }
          /* Remove all gradients and use solid white */
          [class*="gradient"],
          [class*="bg-gradient"],
          .bg-gradient-to-r,
          .medical-gradient,
          .review-submit-header {
            background: white !important;
            background-image: none !important;
          }
          .no-print { display: none !important; }
          .print-header { 
            display: block !important;
            page-break-inside: avoid;
            margin-bottom: 1.5rem;
          }
          .print-only { display: block !important; }
          .review-submit-header { display: none !important; }
          .page-break { page-break-after: always; }
          .section { 
            break-inside: auto;
            page-break-inside: auto;
            margin-bottom: 2rem;
            padding-bottom: 1rem;
          }
          /* Ensure all sections are visible and can flow across pages */
          section#medical,
          section#dental,
          section#procedures {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            page-break-inside: auto;
            break-inside: auto;
            min-height: auto;
          }
          /* Allow sections to break across pages if needed */
          section#medical {
            page-break-after: auto;
          }
          section#dental {
            page-break-after: auto;
            page-break-before: auto;
          }
          section#procedures {
            page-break-before: auto;
          }
          /* Hide progress indicator from PatientFormsWizard and any step indicators */
          .no-print,
          [class*="step"],
          [class*="Step"],
          .flex.items-center.justify-center.py-4 {
            display: none !important;
          }
          /* Ensure dialog content is visible in print */
          [role="dialog"],
          [data-radix-dialog-content],
          [data-radix-dialog-content] > *,
          [class*="DialogContent"],
          [class*="dialog-content"] {
            position: static !important;
            transform: none !important;
            max-width: none !important;
            width: 100% !important;
            max-height: none !important;
            height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            background: white !important;
            border: none !important;
            overflow: visible !important;
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
          }
          /* Remove dialog overlay in print */
          [data-radix-portal],
          [data-radix-dialog-overlay],
          [data-radix-dialog-overlay]::before,
          [data-radix-dialog-overlay]::after,
          [class*="DialogOverlay"],
          [class*="dialog-overlay"] {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
          }
          /* Ensure content flows across pages */
          html, body {
            height: auto !important;
            overflow: visible !important;
            background: white !important;
          }
          /* Ensure all wrapper divs are visible */
          div,
          [class*="overflow"],
          [class*="max-h"],
          [class*="flex-1"],
          [class*="overflow-y-auto"],
          [class*="overflow-y"],
          [class*="overflow-x"] {
            overflow: visible !important;
            max-height: none !important;
            height: auto !important;
            display: block !important;
            visibility: visible !important;
          }
          /* Ensure flex containers don't hide content */
          .flex,
          [class*="flex"],
          [class*="flex-1"] {
            display: flex !important;
            overflow: visible !important;
            max-height: none !important;
            height: auto !important;
          }
          /* Ensure specific dialog classes are visible */
          [class*="DialogContent"],
          [class*="dialog-content"],
          [class*="glass-effect"] {
            background: white !important;
            overflow: visible !important;
            max-height: none !important;
            height: auto !important;
            display: block !important;
          }
          /* Ensure sections and their content are visible */
          section,
          section > *,
          section div,
          section p,
          section h3,
          section h4 {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            color: black !important;
            background: white !important;
          }
          /* Ensure tables are visible */
          table, thead, tbody, tr, td, th {
            display: table !important;
            visibility: visible !important;
            opacity: 1 !important;
            page-break-inside: auto !important;
          }
          /* Ensure grid layouts work in print */
          .grid {
            display: grid !important;
            visibility: visible !important;
          }
          /* Ensure flex layouts work in print */
          .flex {
            display: flex !important;
            visibility: visible !important;
          }
          /* Color adjustments for print - Force all text to black */
          *,
          *::before,
          *::after { 
            background: white !important; 
            color: black !important; 
          }
          /* Override all text color classes to black */
          .text-white,
          [class*="text-white"],
          [class*="text-gray"],
          [class*="text-blue"],
          [class*="text-green"],
          [class*="text-red"],
          [class*="text-yellow"],
          [class*="text-purple"],
          [class*="text-pink"],
          [class*="text-indigo"],
          [class*="text-cyan"],
          [class*="text-emerald"],
          [class*="text-teal"],
          [class*="text-orange"],
          [class*="text-amber"] {
            color: black !important;
          }
          /* Specific gray shades - make them darker for print */
          .text-gray-400,
          [class*="text-gray-400"] { color: #1f2937 !important; }
          .text-gray-500,
          [class*="text-gray-500"] { color: #111827 !important; }
          .text-gray-600,
          [class*="text-gray-600"] { color: #111827 !important; }
          .text-gray-700,
          [class*="text-gray-700"] { color: #000000 !important; }
          .text-gray-800,
          [class*="text-gray-800"] { color: #000000 !important; }
          .text-gray-900,
          [class*="text-gray-900"] { color: #000000 !important; }
          /* Ensure all text elements are black - highest priority */
          p, span, div, h1, h2, h3, h4, h5, h6,
          td, th, li, label, strong, em, b, i, a,
          button, input, textarea, select {
            color: black !important;
          }
          /* Ensure section content is black */
          section,
          section *,
          section p,
          section span,
          section div,
          section h1, section h2, section h3, section h4, section h5, section h6,
          section td, section th, section li {
            color: black !important;
          }
          /* Override any inline styles or computed styles */
          [style*="color"],
          [style*="Color"] {
            color: black !important;
          }
          /* Ensure table text is black */
          table, table *, table td, table th, table p, table span {
            color: black !important;
          }
          /* Ensure grid content is black */
          [class*="grid"] *,
          .grid * {
            color: black !important;
          }
          /* Ensure flex content is black */
          [class*="flex"] *,
          .flex * {
            color: black !important;
          }
          .bg-gray-900,
          [class*="bg-gray-900"] { background: white !important; }
          .bg-gray-800,
          [class*="bg-gray-800"] { background: #f9fafb !important; }
          .bg-gray-700,
          [class*="bg-gray-700"] { background: #f3f4f6 !important; }
          .bg-gray-600,
          [class*="bg-gray-600"] { background: #f9fafb !important; }
          .border-gray-700,
          [class*="border-gray-700"] { border-color: #d1d5db !important; }
          .border-gray-600,
          [class*="border-gray-600"] { border-color: #d1d5db !important; }
          .border-gray-200,
          [class*="border-gray-200"] { border-color: #e5e7eb !important; }
          /* Force visibility of all content - be more specific */
          section#medical,
          section#dental,
          section#procedures {
            visibility: visible !important;
            opacity: 1 !important;
            display: block !important;
            page-break-inside: auto !important;
          }
          section#medical *,
          section#dental *,
          section#procedures * {
            visibility: visible !important;
            opacity: 1 !important;
          }
          /* Ensure inline elements stay inline */
          span, strong, em, b, i, a {
            display: inline !important;
            visibility: visible !important;
          }
          /* Ensure block elements stay block */
          p, h1, h2, h3, h4, h5, h6, div {
            visibility: visible !important;
            opacity: 1 !important;
          }
          /* Ensure table elements */
          table { 
            display: table !important;
            visibility: visible !important;
            width: 100% !important;
          }
          thead { 
            display: table-header-group !important;
            visibility: visible !important;
          }
          tbody { 
            display: table-row-group !important;
            visibility: visible !important;
          }
          tr { 
            display: table-row !important;
            visibility: visible !important;
          }
          td, th { 
            display: table-cell !important;
            visibility: visible !important;
          }
          /* Ensure grid and flex layouts work */
          [class*="grid"] {
            display: grid !important;
            visibility: visible !important;
          }
          [class*="flex"]:not(.no-print) {
            display: flex !important;
            visibility: visible !important;
          }
          /* Ensure container doesn't hide content */
          div {
            overflow: visible !important;
            max-height: none !important;
            height: auto !important;
          }
          /* Ensure all content containers are visible */
          .flex-1,
          .overflow-y-auto,
          [class*="overflow"] {
            overflow: visible !important;
            max-height: none !important;
            height: auto !important;
          }
          /* Ensure sections are fully visible */
          section#medical,
          section#dental,
          section#procedures,
          section#medical *,
          section#dental *,
          section#procedures * {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            color: black !important;
            background: white !important;
          }
          /* Ensure inline elements are visible */
          span, p, div, h1, h2, h3, h4, h5, h6 {
            display: inline-block !important;
            visibility: visible !important;
            opacity: 1 !important;
          }
          p, h1, h2, h3, h4, h5, h6 {
            display: block !important;
          }
          /* Table styling for print */
          table {
            page-break-inside: auto;
            width: 100%;
            border-collapse: collapse;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          thead {
            display: table-header-group;
          }
          tfoot {
            display: table-footer-group;
          }
          /* Ensure dental findings grid is visible */
          .grid {
            display: grid !important;
          }
          /* Ensure all content is visible */
          p, h1, h2, h3, h4, h5, h6 {
            display: block !important;
            visibility: visible !important;
          }
          /* Ensure flex containers work in print */
          .flex {
            display: flex !important;
          }
          /* Ensure grid containers work in print */
          .grid {
            display: grid !important;
          }
        }
        .print-only { display: none; }
      `}</style>

      <div id="patient-record-content" className={`${hideDialog ? 'bg-transparent' : 'max-w-5xl mx-auto bg-white rounded-xl shadow-sm border border-gray-200'} overflow-hidden print:shadow-none print:border-0 print:max-w-none print:overflow-visible print:bg-white`}>
        {/* Print Header - Visible when printing or in view mode */}
        <div className={`${hideDialog ? 'block' : 'print-only'} print-header mb-6 pb-4 border-b-2 print:border-black ${hideDialog ? 'border-gray-700' : 'border-gray-300'}`}>
          <div className="flex justify-between items-start print:flex print:justify-between">
            {/* Hospital Information - Left Side */}
            <div className="flex-1 print:flex-1">
              <div className="flex items-center gap-3 mb-2 print:flex print:items-center">
                <img 
                  src="/logo.png" 
                  alt="Gugan Dental Care Logo" 
                  className="w-16 h-16 object-contain print:w-12 print:h-12"
                  onError={(e) => {
                    // Fallback to text if image fails to load
                    e.target.style.display = 'none';
                    const fallback = e.target.nextElementSibling;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xl print:bg-white print:border print:border-black print:text-black hidden">
                  GDC
                </div>
                <div>
                  <h1 className={`text-2xl font-bold print:text-black print:text-xl ${hideDialog ? 'text-white' : 'text-gray-900'}`}>Gugan Dental Care</h1>
                  <p className={`text-sm print:text-black print:text-xs uppercase ${hideDialog ? 'text-gray-400' : 'text-gray-600'}`}>GOBICHETTIPALAYAM</p>
                </div>
              </div>
              <div className={`text-sm print:text-black print:text-xs space-y-1 ${hideDialog ? 'text-gray-300' : 'text-gray-700'}`}>
                <p className="print:m-0">{profile?.clinic_name || profile?.address || "GRK Complex, Bawa Medical Upstairs, Near Bustand Signal"}</p>
                <p className="print:m-0">{profile?.clinic_name || profile?.address ? "" : "Gobichettipalayam – 638452"}</p>
                <p className="print:m-0">Phone: {profile?.phone || "+91 96299 96774"}</p>
                <p className="print:m-0">Email: {profile?.email || "gugandentalcare@gmail.com"}</p>
              </div>
            </div>
            
            {/* Patient Information - Right Side */}
            <div className="flex-1 text-right print:flex-1 print:text-right">
              <h2 className={`text-xl font-bold print:text-black print:text-lg mb-3 print:mb-2 ${hideDialog ? 'text-white' : 'text-gray-900'}`}>Patient Record</h2>
              <div className={`text-sm print:text-black print:text-xs space-y-1 ${hideDialog ? 'text-gray-300' : 'text-gray-700'}`}>
                <p className="print:m-0"><span className="font-semibold">Patient Name:</span> {patientInfo.name || "N/A"}</p>
                <p className="print:m-0"><span className="font-semibold">Appointment ID:</span> {appointmentDetails?.referenceId || appointmentId || "N/A"}</p>
                <p className="print:m-0"><span className="font-semibold">Phone:</span> {patientInfo.phone || "N/A"}</p>
                <p className="print:m-0"><span className="font-semibold">Address:</span> {patientInfo.address || "N/A"}</p>
                <p className="print:m-0"><span className="font-semibold">Date:</span> {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Screen Header - Hidden when printing or in view mode */}
        {!hideDialog && (
          <div className={`review-submit-header bg-gradient-to-r from-indigo-50 to-white px-6 py-6 border-b border-gray-200`}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-gray-800">Review & Submit</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Please review all information before final submission
                </p>
              </div>
              <div className="no-print flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={exportJSON}
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white border-0 hover:from-blue-600 hover:to-cyan-600 shadow-md hover:shadow-lg transition-all"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export JSON
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={printPage}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 hover:from-purple-600 hover:to-pink-600 shadow-md hover:shadow-lg transition-all"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print / PDF
                </Button>
              </div>
            </div>

            {/* Issues banner */}
            {issues.length > 0 && (
              <div className="no-print mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800">
                      {issues.length} item{issues.length > 1 ? "s" : ""} need your attention
                    </p>
                    <ul className="mt-2 space-y-1">
                      {issues.map((it, i) => (
                        <li key={i} className="text-sm text-amber-800 flex items-center justify-between">
                          <span>
                            <span className="font-medium">{it.section}:</span> {it.msg}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(it.idx)}
                            className="ml-2 bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200 text-xs"
                          >
                            Fix
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Progress */}
            {submitting && (
              <div className="no-print mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                <span>{submitStep || "Submitting…"}</span>
              </div>
            )}

            {submitError && !submitting && (
              <div className="no-print mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {submitError}
              </div>
            )}
          </div>
        )}

        <div className={`px-6 py-6 space-y-10 print:px-0 print:py-4 print:space-y-8 ${hideDialog ? 'bg-gray-900/30' : 'bg-white'} print:bg-white print:block print:visible`}>
          {/* Medical History Section */}
          <section id="medical" className={`section border-b pb-8 print:pb-4 ${hideDialog ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-start justify-between mb-5">
              <h3 className={`text-lg font-semibold ${hideDialog ? 'text-white' : 'text-gray-800'}`}>Medical History</h3>
              {!hideDialog && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(2)}
                  className="no-print bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-0 hover:from-indigo-600 hover:to-purple-600 shadow-md hover:shadow-lg transition-all"
                >
                  Edit
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className={`font-medium text-white`}>Surgery/Hospitalization (last 5 years)</p>
                <p className={`text-sm ${hideDialog ? 'text-gray-400' : 'text-gray-500'}`}>
                  {currentMedicalHistory?.surgery_hospitalized || "Not specified"}
                  {currentMedicalHistory?.surgery_hospitalized === 'Yes' && currentMedicalHistory?.surgery_details
                    ? ` — ${currentMedicalHistory.surgery_details}`
                    : ""}
                </p>
              </div>

              <div>
                <p className={`font-medium text-white`}>Current Fever/Cold/Cough</p>
                <p className={`text-sm ${hideDialog ? 'text-gray-400' : 'text-gray-500'}`}>
                  {currentMedicalHistory?.fever_cold_cough || "Not specified"}
                  {currentMedicalHistory?.fever_cold_cough === 'Yes' && currentMedicalHistory?.symptoms_details
                    ? ` — ${currentMedicalHistory.symptoms_details}`
                    : ""}
                </p>
              </div>

              <div>
                <p className={`font-medium text-white`}>Abnormal Bleeding (injury/operation)</p>
                <p className={`text-sm ${hideDialog ? 'text-gray-400' : 'text-gray-500'}`}>
                  {currentMedicalHistory?.abnormal_bleeding || "Not specified"}
                  {currentMedicalHistory?.abnormal_bleeding === 'Yes' && currentMedicalHistory?.abnormal_bleeding_details
                    ? ` — ${currentMedicalHistory.abnormal_bleeding_details}`
                    : ""}
                </p>
              </div>

              <div>
                <p className={`font-medium text-white`}>Currently Taking Medicine</p>
                <p className={`text-sm ${hideDialog ? 'text-gray-400' : 'text-gray-500'}`}>
                  {currentMedicalHistory?.taking_medicine || "Not specified"}
                  {currentMedicalHistory?.taking_medicine === 'Yes' && currentMedicalHistory?.medicine_details
                    ? ` — ${currentMedicalHistory.medicine_details}`
                    : ""}
                </p>
              </div>

              <div>
                <p className={`font-medium text-white`}>Medication Allergy</p>
                <p className={`text-sm ${hideDialog ? 'text-gray-400' : 'text-gray-500'}`}>
                  {currentMedicalHistory?.allergic_medication || "Not specified"}
                  {currentMedicalHistory?.allergic_medication === 'Yes' && currentMedicalHistory?.allergy_details
                    ? ` — ${currentMedicalHistory.allergy_details}`
                    : ""}
                </p>
              </div>

              <div className="md:col-span-2">
                <p className={`font-medium text-white`}>Past Dental History</p>
                <p className={`text-sm ${hideDialog ? 'text-gray-400' : 'text-gray-500'}`}>
                  {currentMedicalHistory?.past_dental_history || "Not specified"}
                </p>
              </div>

              <div className="md:col-span-2">
                <p className={`font-medium mb-2 text-white`}>Medical Problems</p>
                <div className="flex flex-wrap gap-2">
                  {medicalProblems.length > 0 ? (
                    medicalProblems.map((prob, i) => (
                      <span
                        key={i}
                        className={`px-3 py-1.5 text-xs rounded-full font-medium shadow-sm ${
                          hideDialog 
                            ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-300 border border-blue-500/30' 
                            : 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 border border-blue-200'
                        }`}
                      >
                        {prob}
                      </span>
                    ))
                  ) : (
                    <span className={hideDialog ? "text-gray-400" : "text-gray-500"}>No problems reported</span>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Dental Examination Section */}
          <section id="dental" className={`section border-b pb-8 print:pb-4 ${hideDialog ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-start justify-between mb-5">
              <h3 className={`text-lg font-semibold ${hideDialog ? 'text-white' : 'text-gray-800'}`}>Dental Examination</h3>
              {!hideDialog && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(3)}
                  className="no-print bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0 hover:from-emerald-600 hover:to-teal-600 shadow-md hover:shadow-lg transition-all"
                >
                  Edit
                </Button>
              )}
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className={`font-medium text-white`}>Chief Complaint</p>
                  <p className={`text-sm ${hideDialog ? 'text-gray-400' : 'text-gray-500'}`}>
                    {currentDentalExamination?.chief_complaint || "Not specified"}
                  </p>
                </div>
                <div>
                  <p className={`font-medium text-white`}>Duration & Onset</p>
                  <p className={`text-sm ${hideDialog ? 'text-gray-400' : 'text-gray-500'}`}>
                    {currentDentalExamination?.duration_onset || "Not specified"}
                  </p>
                </div>
              </div>

              <div>
                <p className={`font-medium text-white`}>Trigger Factors</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {triggerFactors.length > 0 ? (
                    triggerFactors.map((factor, i) => (
                      <span
                        key={i}
                        className={`px-3 py-1.5 text-xs rounded-full font-medium shadow-sm ${
                          hideDialog 
                            ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-300 border border-green-500/30' 
                            : 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200'
                        }`}
                      >
                        {factor}
                      </span>
                    ))
                  ) : (
                    <span className={hideDialog ? "text-gray-400" : "text-gray-500"}>None reported</span>
                  )}
                </div>
              </div>

              {/* Tooth Findings */}
              <div>
                {renderDentalFindingsGrid()}
              </div>

              <div>
                <p className={`font-medium text-white`}>Diagnosis Notes</p>
                <p className={`text-sm ${hideDialog ? 'text-gray-400' : 'text-gray-500'}`}>
                  {currentDentalExamination?.diagnosis_notes || "No diagnosis notes"}
                </p>
              </div>

              <div>
                <p className={`font-medium text-white`}>Treatment Plan Notes</p>
                <p className={`text-sm ${hideDialog ? 'text-gray-400' : 'text-gray-500'}`}>
                  {currentDentalExamination?.treatment_plan_notes || "No treatment plan notes"}
                </p>
              </div>
            </div>
          </section>

          {/* Procedures Section */}
          <section id="procedures" className="section print:pb-4">
            <div className="flex items-start justify-between mb-5">
              <h3 className={`text-lg font-semibold ${hideDialog ? 'text-white' : 'text-gray-800'}`}>Procedures</h3>
              {!hideDialog && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(4)}
                  className="no-print bg-gradient-to-r from-orange-500 to-red-500 text-white border-0 hover:from-orange-600 hover:to-red-600 shadow-md hover:shadow-lg transition-all"
                >
                  Edit
                </Button>
              )}
            </div>

            <div className={`overflow-x-auto border rounded-lg ${hideDialog ? 'border-blue-500/30 bg-gradient-to-br from-blue-900/40 via-blue-800/30 to-purple-900/40 backdrop-blur-sm' : 'border-gray-200 bg-white'}`}>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={hideDialog ? "bg-blue-900/50" : "bg-gray-50"}>
                  <tr>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${hideDialog ? 'text-gray-300' : 'text-gray-500'}`}>Date</th>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${hideDialog ? 'text-gray-300' : 'text-gray-500'}`}>Procedure</th>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${hideDialog ? 'text-gray-300' : 'text-gray-500'}`}>Next Appt</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${hideDialog ? 'bg-transparent divide-blue-800/30' : 'bg-white divide-gray-200'}`}>
                  {procedureRows.length > 0 ? (
                    procedureRows.map((r, i) => (
                      <tr key={i} className={hideDialog ? "hover:bg-blue-900/30" : "hover:bg-gray-50"}>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm ${hideDialog ? 'text-white' : 'text-gray-900'}`}>
                          {r.visitDate ? formatDate(r.visitDate) : (r.visit_date ? formatDate(r.visit_date) : "Not specified")}
                        </td>
                        <td className={`px-4 py-3 text-sm ${hideDialog ? 'text-white' : 'text-gray-900'}`}>
                          {r.procedure || "Not specified"}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm ${hideDialog ? 'text-white' : 'text-gray-900'}`}>
                          {r.nextApptDate ? formatDate(r.nextApptDate) : (r.next_appt_date ? formatDate(r.next_appt_date) : "Not specified")}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className={hideDialog ? "hover:bg-blue-900/30" : "hover:bg-gray-50"}>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm ${hideDialog ? 'text-white' : 'text-gray-900'}`}>
                        Not specified
                      </td>
                      <td className={`px-4 py-3 text-sm ${hideDialog ? 'text-white' : 'text-gray-900'}`}>
                        Not specified
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm ${hideDialog ? 'text-white' : 'text-gray-900'}`}>
                        Not specified
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Payments Section */}
          <section id="payments" className="section print:pb-4 mt-6">
            <div className="flex items-start justify-between mb-5">
              <h3 className={`text-lg font-semibold ${hideDialog ? 'text-white' : 'text-gray-800'}`}>Payments</h3>
              {!hideDialog && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(4)}
                  className="no-print bg-gradient-to-r from-orange-500 to-red-500 text-white border-0 hover:from-orange-600 hover:to-red-600 shadow-md hover:shadow-lg transition-all"
                >
                  Edit
                </Button>
              )}
            </div>

            <div className={`overflow-x-auto border rounded-lg ${hideDialog ? 'border-green-500/30 bg-gradient-to-br from-green-900/40 via-emerald-800/30 to-teal-900/40 backdrop-blur-sm' : 'border-gray-200 bg-white'}`}>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={hideDialog ? "bg-green-900/50" : "bg-gray-50"}>
                  <tr>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${hideDialog ? 'text-gray-300' : 'text-gray-500'}`}>Date</th>
                    <th className={`px-4 py-3 text-right text-xs font-medium uppercase ${hideDialog ? 'text-gray-300' : 'text-gray-500'}`}>Total</th>
                    <th className={`px-4 py-3 text-right text-xs font-medium uppercase ${hideDialog ? 'text-gray-300' : 'text-gray-500'}`}>Paid</th>
                    <th className={`px-4 py-3 text-right text-xs font-medium uppercase ${hideDialog ? 'text-gray-300' : 'text-gray-500'}`}>Due</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${hideDialog ? 'bg-transparent divide-green-800/30' : 'bg-white divide-gray-200'}`}>
                  {procedureRows.length > 0 ? (
                    procedureRows.map((r, i) => (
                      <tr key={i} className={hideDialog ? "hover:bg-green-900/30" : "hover:bg-gray-50"}>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm ${hideDialog ? 'text-white' : 'text-gray-900'}`}>
                          {r.visitDate ? formatDate(r.visitDate) : (r.visit_date ? formatDate(r.visit_date) : "Not specified")}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${hideDialog ? 'text-white' : 'text-gray-900'}`}>
                          {formatCurrency(r.total || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${hideDialog ? 'text-green-300' : 'text-green-600'}`}>
                          {formatCurrency(r.paid || 0)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                          <span className={Number(r.due || 0) > 0 ? (hideDialog ? "text-red-300" : "text-red-600") : (hideDialog ? "text-green-300" : "text-green-600")}>
                            {formatCurrency(r.due || 0)}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className={hideDialog ? "hover:bg-green-900/30" : "hover:bg-gray-50"}>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm ${hideDialog ? 'text-white' : 'text-gray-900'}`}>
                        Not specified
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${hideDialog ? 'text-white' : 'text-gray-900'}`}>
                        {formatCurrency(0)}
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${hideDialog ? 'text-green-300' : 'text-green-600'}`}>
                        {formatCurrency(0)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                        <span className={hideDialog ? "text-green-300" : "text-green-600"}>
                          {formatCurrency(0)}
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot className={hideDialog ? "bg-green-900/50" : "bg-gray-50"}>
                  <tr>
                    <th className={`px-4 py-3 text-right text-sm font-medium uppercase ${hideDialog ? 'text-gray-300' : 'text-gray-500'}`}>
                      Totals
                    </th>
                    <th className={`px-4 py-3 text-right text-sm font-semibold ${hideDialog ? 'text-white' : 'text-gray-900'}`}>
                      {formatCurrency(totals.total)}
                    </th>
                    <th className={`px-4 py-3 text-right text-sm font-semibold ${hideDialog ? 'text-green-300' : 'text-green-700'}`}>
                      {formatCurrency(totals.paid)}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">
                      <span className={totals.due > 0 ? (hideDialog ? "text-red-400" : "text-red-700") : (hideDialog ? "text-green-400" : "text-green-700")}>
                        {formatCurrency(totals.due)}
                      </span>
                    </th>
                  </tr>
                </tfoot>
              </table>
            </div>

            {hasDue && (
              <div className={`mt-3 rounded-md border p-3 text-sm ${
                hideDialog 
                  ? 'border-amber-500/30 bg-amber-500/10 text-amber-300' 
                  : 'border-amber-200 bg-amber-50 text-amber-800'
              }`}>
                There is an outstanding balance. You can still submit and update payments later.
              </div>
            )}
          </section>

          {/* Submitted By Section */}
          <section className="mt-6 pt-4 border-t border-gray-300 print:border-gray-400">
            <div className={`text-sm ${hideDialog ? 'text-white' : 'text-gray-700'}`}>
              <span className="font-semibold">Submitted By:</span> Dr. {doctorName || profile?.full_name || profile?.name || 'N/A'}
            </div>
          </section>

          {/* Actions - Always show submit button */}
          {onSubmit && (
            <div className={`no-print mt-8 flex justify-between pt-4 border-t ${hideDialog ? 'border-gray-700' : 'border-gray-200'}`}>
              {onBack && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onBack}
                  className="bg-gradient-to-r from-gray-500 to-gray-600 text-white border-0 hover:from-gray-600 hover:to-gray-700 shadow-md hover:shadow-lg transition-all"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              )}
              {!onBack && <div />}
              <Button
                type="button"
                onClick={openConfirm}
                disabled={submitting}
                className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white border-0 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 disabled:opacity-50 shadow-md hover:shadow-lg transition-all font-semibold"
              >
                {submitting ? "Submitting…" : "Submit Patient Record"}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmOpen && (
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Confirm submission</DialogTitle>
              <DialogDescription>
                Please confirm you want to submit this patient record. You can edit it later.
              </DialogDescription>
            </DialogHeader>

            {hasDue && (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Note: There is an outstanding balance due.
              </div>
            )}

            <div className="mt-4 grid grid-cols-1 gap-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Patient ID</span>
                <span className="font-medium">{patientInfo.id || patientId || "Not specified"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Patient Name</span>
                <span className="font-medium">{patientInfo.name || patientName || `Patient #${patientId || 'N/A'}`}</span>
              </div>
              {appointmentId && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Appointment ID</span>
                  <span className="font-medium">{appointmentDetails?.referenceId || appointmentId}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Attended by</span>
                <span className="font-medium">
                  {profile?.full_name || profile?.name || "Not specified"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Date</span>
                <span className="font-medium">
                  {new Date().toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Chief Complaint</span>
                <span className="font-medium">
                  {currentDentalExamination?.chief_complaint || "—"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Procedures</span>
                <span className="font-medium">
                  {procedureRows.length} item{procedureRows.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total</span>
                <span className="font-semibold">{formatCurrency(totals.total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Paid</span>
                <span className="font-semibold text-green-700">{formatCurrency(totals.paid)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Due</span>
                <span className={`font-semibold ${totals.due > 0 ? "text-red-700" : "text-green-700"}`}>
                  {formatCurrency(totals.due)}
                </span>
              </div>
            </div>

            {submitError && (
              <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                {submitError}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => !submitting && setConfirmOpen(false)}
                disabled={submitting}
                className="bg-gradient-to-r from-gray-500 to-gray-600 text-white border-0 hover:from-gray-600 hover:to-gray-700 disabled:opacity-60 shadow-md hover:shadow-lg transition-all font-medium"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={doSubmit}
                disabled={submitting}
                className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white border-0 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all font-semibold"
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Submitting…
                  </span>
                ) : (
                  "Confirm & Submit"
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Modals */}
      {editSection === 2 && (
        <MedicalHistoryForm
          open={true}
          onClose={() => {
            setEditSection(null);
            // Refetch data after closing
            refetchData();
          }}
          patientId={patientId}
          patientName={patientName}
          appointmentId={appointmentId}
          onNext={async () => {
            setEditSection(null);
            // Refetch data
            await refetchData();
          }}
        />
      )}

      {editSection === 3 && (
        <DentalExaminationForm
          open={true}
          onClose={() => {
            setEditSection(null);
            // Refetch data after closing
            refetchData();
          }}
          patientId={patientId}
          patientName={patientName}
          onComplete={async () => {
            setEditSection(null);
            // Refetch data
            await refetchData();
          }}
          hideDialog={false}
        />
      )}

      {editSection === 4 && (
        <ProcedureTrackingForm
          open={true}
          onClose={() => {
            setEditSection(null);
            // Refetch data after closing
            refetchData();
          }}
          patientId={patientId}
          patientName={patientName}
          medicalHistory={currentMedicalHistory}
          dentalExamination={currentDentalExamination}
          onNext={async () => {
            setEditSection(null);
            // Refetch data
            await refetchData();
          }}
        />
      )}
    </>
  );

  // If hideDialog is true, return only content (for wizard modal)
  if (hideDialog) {
    return contentWrapper;
  }

  // Otherwise, return as full page
  return (
    <div className="min-h-screen bg-white print:bg-white">
      {contentWrapper}
    </div>
  );
};

export default ReviewSubmitPage;

